import React from 'react';
import {
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { useSort, compareValues } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';
import { aggregateBreakdown } from '../helpers/aggregateBreakdown';

function sortBreakdown(breakdown, orderBy, order) {
    return [...breakdown].sort((a, b) => {
        if (orderBy === 'totalSales' || orderBy === 'percentageOfTotal') {
            return compareValues(a.totalSales, b.totalSales, order);
        }
        if (orderBy === 'totalPax') {
            return compareValues(a.itemCount, b.itemCount, order);
        }
        const ttCmp = compareValues(a.ticketTypeName, b.ticketTypeName, order);
        if (ttCmp !== 0) return ttCmp;
        return compareValues(a.ticketOptionName, b.ticketOptionName, order);
    });
}

// Display label for a group row. Real affiliates use their name; the "No
// affiliate" bucket (and its source sub-rows when broken down) use localized
// labels, except per-agent rows which show the agent's name.
function getGroupLabel(item, t) {
    if (item.commissionGroupId !== null) {
        return item.affiliateName;
    }
    switch (item.noAffiliateType) {
        case 'online':
            return t('reports.affiliateSales.online');
        case 'agents':
            return t('reports.affiliateSales.agentsNoGroup');
        case 'agent':
            return item.affiliateName ?? t('reports.affiliateSales.noAffiliateSection');
        default:
            return t('reports.affiliateSales.noAffiliateSection');
    }
}

function getRowKey(item) {
    if (item.commissionGroupId != null) return `cg:${item.commissionGroupId}`;
    if (item.noAffiliateType === 'agent') return `agent:${item.agentId}`;
    return `na:${item.noAffiliateType ?? 'all'}`;
}

function getBreakdownKey(bd, showTicketType, showTicketOption) {
    if (showTicketType && showTicketOption) return `${bd.ticketTypeId}-${bd.ticketOptionId}`;
    return showTicketType ? `tt-${bd.ticketTypeId}` : `to-${bd.ticketOptionId}`;
}

const AffiliateSalesReportTable = ({
    items,
    totalSales,
    totalBookings,
    totalPax,
    showTicketType = true,
    showTicketOption = true,
}) => {
    const showBreakdown = showTicketType || showTicketOption;
    const { t } = useTranslation();
    const sort = useSort(items, 'affiliateName', 'asc');

    // Real affiliates first; the "No affiliate" bucket (and its source rows
    // when broken down) is pinned to the bottom regardless of the active sort.
    const affiliateItems = sort.sortedItems.filter((item) => item.commissionGroupId !== null);
    const noAffiliateItems = sort.sortedItems.filter((item) => item.commissionGroupId === null);

    // When the No-affiliate bucket is broken down into source rows, show a
    // combined "No affiliate" subtotal header above them.
    const noAffiliateBrokenDown = noAffiliateItems.some(
        (item) => item.noAffiliateType && item.noAffiliateType !== 'all',
    );
    const noAffiliateSubtotal = noAffiliateBrokenDown
        ? noAffiliateItems.reduce(
              (acc, item) => ({
                  bookingCount: acc.bookingCount + item.bookingCount,
                  totalPax: acc.totalPax + item.totalPax,
                  totalSales: acc.totalSales + item.totalSales,
                  percentageOfTotal: acc.percentageOfTotal + item.percentageOfTotal,
              }),
              { bookingCount: 0, totalPax: 0, totalSales: 0, percentageOfTotal: 0 },
          )
        : null;

    const totalPercentage = totalSales === 0 ? 0 : 100;

    const renderGroup = (item, withTopBorder) => {
        const isNoAffiliate = item.commissionGroupId === null;
        const breakdown = showBreakdown
            ? sortBreakdown(
                  aggregateBreakdown(item.breakdown || [], showTicketType, showTicketOption),
                  sort.orderBy,
                  sort.order,
              )
            : [];
        return (
            <React.Fragment key={getRowKey(item)}>
                <TableRow
                    sx={(theme) => ({
                        backgroundColor: isNoAffiliate
                            ? theme.palette.action.selected
                            : theme.palette.action.hover,
                        borderTop: withTopBorder ? `2px solid ${theme.palette.divider}` : undefined,
                    })}
                >
                    <TableCell>
                        <Box
                            component="strong"
                            sx={isNoAffiliate ? { fontStyle: 'italic' } : undefined}
                        >
                            {getGroupLabel(item, t)}
                        </Box>
                    </TableCell>
                    {showTicketType && <TableCell />}
                    {showTicketOption && <TableCell />}
                    <TableCell align="right">
                        <strong>{item.bookingCount}</strong>
                    </TableCell>
                    <TableCell align="right">
                        <strong>{item.totalPax}</strong>
                    </TableCell>
                    <TableCell align="right">
                        <strong>{formatCentsToUsd(item.totalSales)}</strong>
                    </TableCell>
                    <TableCell align="right">
                        <strong>{item.percentageOfTotal.toFixed(2)}%</strong>
                    </TableCell>
                </TableRow>
                {showBreakdown &&
                    breakdown.map((bd) => (
                        <TableRow
                            key={getBreakdownKey(bd, showTicketType, showTicketOption)}
                            sx={{ backgroundColor: isNoAffiliate ? 'action.hover' : undefined }}
                        >
                            <TableCell />
                            {showTicketType && (
                                <TableCell sx={{ color: 'text.secondary' }}>
                                    {bd.ticketTypeName ?? '-'}
                                </TableCell>
                            )}
                            {showTicketOption && (
                                <TableCell sx={{ color: 'text.secondary' }}>
                                    {bd.ticketOptionName ?? '-'}
                                </TableCell>
                            )}
                            <TableCell />
                            <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                {bd.itemCount}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                {formatCentsToUsd(bd.totalSales)}
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    ))}
            </React.Fragment>
        );
    };

    return (
        <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table stickyHeader sx={{ width: '100%', '& thead th': { top: 0 } }}>
                <TableHead>
                    <TableRow>
                        <SortableHeaderCell field="affiliateName" sort={sort}>
                            {t('reports.affiliateSales.affiliateName')}
                        </SortableHeaderCell>
                        {showTicketType && (
                            <TableCell>{t('reports.affiliateSales.ticketType')}</TableCell>
                        )}
                        {showTicketOption && (
                            <TableCell>{t('reports.affiliateSales.ticketOption')}</TableCell>
                        )}
                        <SortableHeaderCell field="bookingCount" sort={sort} align="right">
                            {t('reports.affiliateSales.bookingCount')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="totalPax" sort={sort} align="right">
                            {t('reports.affiliateSales.pax')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="totalSales" sort={sort} align="right">
                            {t('reports.affiliateSales.totalSales')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="percentageOfTotal" sort={sort} align="right">
                            {t('reports.affiliateSales.percentageOfTotal')}
                        </SortableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {affiliateItems.map((item) => renderGroup(item, false))}
                    {noAffiliateSubtotal && (
                        <TableRow
                            sx={(theme) => ({
                                backgroundColor: theme.palette.action.selected,
                                borderTop: `2px solid ${theme.palette.divider}`,
                            })}
                        >
                            <TableCell>
                                <Box component="strong" sx={{ fontStyle: 'italic' }}>
                                    {t('reports.affiliateSales.noAffiliateSection')}
                                </Box>
                            </TableCell>
                            {showTicketType && <TableCell />}
                            {showTicketOption && <TableCell />}
                            <TableCell align="right">
                                <strong>{noAffiliateSubtotal.bookingCount}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{noAffiliateSubtotal.totalPax}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(noAffiliateSubtotal.totalSales)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{noAffiliateSubtotal.percentageOfTotal.toFixed(2)}%</strong>
                            </TableCell>
                        </TableRow>
                    )}
                    {noAffiliateItems.map((item) => renderGroup(item, !noAffiliateBrokenDown))}
                    <TableRow sx={{ backgroundColor: 'action.selected' }}>
                        <TableCell
                            colSpan={1 + (showTicketType ? 1 : 0) + (showTicketOption ? 1 : 0)}
                        >
                            <strong>{t('reports.summary.grandTotal', 'Grand Total')}</strong>
                        </TableCell>
                        <TableCell align="right">
                            <strong>{totalBookings}</strong>
                        </TableCell>
                        <TableCell align="right">
                            <strong>{totalPax ?? ''}</strong>
                        </TableCell>
                        <TableCell align="right">
                            <strong>{formatCentsToUsd(totalSales)}</strong>
                        </TableCell>
                        <TableCell align="right">
                            <strong>{totalPercentage.toFixed(2)}%</strong>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default AffiliateSalesReportTable;
